export type MockGrain = 'region' | 'market' | 'location' | 'locationType' | 'year' | 'period' | 'week';
export type MockMetric = 'nonAsnLoads' | 'totalLoads' | 'pctNonAsn' | 'pyPct' | 'avgSkus' | 'caseQty';

/**
 * Flattened UI row shape consumed by LocationSummaryTableRow, LocationSummaryTotalRow,
 * LocationSummaryTableHeader, and the hierarchy builders. This is the adapter-layer
 * type that sits between the nested API response (ASNLocationDetailsRow) and the
 * rendering components. All grain fields are plain strings; metric fields are numbers.
 *
 * Important: `pctNonAsn` is included as an explicit field so that `metricValue` can
 * index it safely without a dynamic `row[key]` lookup. The value is populated from
 * `vals.pctNonAsn` in the adaptApiRows mapping in LocationSummaryV2Table.tsx.
 */
export interface MockLocationRow {
  id: string;
  // ── Grain (dimension) fields ─────────────────────────────────────────────
  region: string;
  market: string;
  location: string;
  locationType: string;
  year: string;
  period: string;
  week: string;
  // ── Metric fields ────────────────────────────────────────────────────────
  nonAsnLoads: number;
  totalLoads: number;
  /** Non-ASN percentage — explicit field so metricValue can index it safely. */
  pctNonAsn: number;
  pyPct: number;
  avgSkus: number;
  caseQty: number;
  // ── Hierarchy metadata ───────────────────────────────────────────────────
  /** Optional — present on rows produced by buildRegionHierarchyRows /
   *  buildMarketHierarchyRows; absent on flat adapted API rows. */
  linkedLocations?: string[];
  hierarchyLevel?: 0 | 1 | 2;
  parentId?: string;
  groupType?: 'region' | 'market' | 'location';
  parentMarketExpandKey?: string;
  parentRegionExpandKey?: string;
}

export const MOCK_GRAINS: ReadonlyArray<{ key: MockGrain; label: string }> = [
  { key: 'region', label: 'Region / Unit' },
  { key: 'market', label: 'Market / POD' },
  { key: 'location', label: 'Location' },
  { key: 'locationType', label: 'Location Type' },
  { key: 'year', label: 'Year' },
  { key: 'period', label: 'Period' },
  { key: 'week', label: 'Week' },
];

export const MOCK_METRICS: ReadonlyArray<{ key: MockMetric; label: string; format: 'int' | 'pct' | 'decimal' }> = [
  { key: 'nonAsnLoads', label: 'Non-ASN Loads', format: 'int' },
  { key: 'totalLoads', label: 'Total Loads', format: 'int' },
  { key: 'pctNonAsn', label: '% Non-ASN', format: 'pct' },
  { key: 'pyPct', label: 'PY %', format: 'pct' },
  { key: 'avgSkus', label: 'Avg SKUs', format: 'decimal' },
  { key: 'caseQty', label: 'Case Qty', format: 'int' },
];

/**
 * Returns the numeric value for a given metric key from a flattened MockLocationRow.
 *
 * Uses an explicit field map instead of dynamic `row[metric]` index access so that
 * TypeScript can verify every branch is covered and the return type is always `number`.
 * `pctNonAsn` is pre-populated on the row from the API response (not recomputed here)
 * so the value is always backend-authoritative; the explicit branch remains for cases
 * where the value might be 0 due to a zero totalLoads (avoids NaN from 0/0).
 */
export const metricValue = (row: MockLocationRow, metric: MockMetric): number => {
  switch (metric) {
    case 'nonAsnLoads': return row.nonAsnLoads;
    case 'totalLoads':  return row.totalLoads;
    case 'pctNonAsn':   return row.pctNonAsn;
    case 'pyPct':       return row.pyPct;
    case 'avgSkus':     return row.avgSkus;
    case 'caseQty':     return row.caseQty;
  }
};

/**
 * Formats a metric value for display.
 *
 * Defensively guards against non-finite runtime values (null, undefined, NaN,
 * Infinity) that can arrive from the API despite the TypeScript type saying
 * `number`. This is the last line of defence before a `.toFixed()` call would
 * crash with "Cannot read properties of undefined (reading 'toFixed')".
 * Returns '—' (en-dash) for any non-finite value so the cell is visibly empty
 * rather than silently wrong.
 */
export const formatMetric = (value: number, metric: MockMetric): string => {
  if (value == null || !Number.isFinite(value as number)) return '—';
  if (metric === 'pctNonAsn' || metric === 'pyPct') return `${value.toFixed(2)}%`;
  if (metric === 'avgSkus') return value.toFixed(1);
  return Math.round(value).toLocaleString('en-US');
};

export const buildRegionHierarchyRows = (sourceRows: MockLocationRow[]): MockLocationRow[] => {
  const regions = new Map<string, MockLocationRow[]>();
  sourceRows.forEach((row) => {
    const regionRows = regions.get(row.region) ?? [];
    regionRows.push(row);
    regions.set(row.region, regionRows);
  });

  const result: MockLocationRow[] = [];
  regions.forEach((regionRows, region) => {
    const regionExpandKey = `region-${region}`;
    const regionTotal = regionRows.reduce((total, row) => total + row.totalLoads, 0);
    const regionNonAsn = regionRows.reduce((total, row) => total + row.nonAsnLoads, 0);
    // Region group row: set region to the region name; clear market and location
    // so that row[grain] returns the correct value for each column.
    // Previously "location: region" was used as a catch-all label field, but
    // the row renderer reads row[grain] directly — so market and location cells
    // on a region group row must be blank, not echoed from the first child.
    result.push({
      ...regionRows[0],
      id: regionExpandKey,
      region,
      market: '',
      location: '',
      nonAsnLoads: regionNonAsn,
      totalLoads: regionTotal,
      pyPct: regionRows.reduce((total, row) => total + row.pyPct, 0) / regionRows.length,
      avgSkus: regionRows.reduce((total, row) => total + row.avgSkus, 0) / regionRows.length,
      caseQty: regionRows.reduce((total, row) => total + row.caseQty, 0),
      hierarchyLevel: 0,
      parentId: region,
      groupType: 'region',
      parentMarketExpandKey: undefined,
      parentRegionExpandKey: undefined,
    });

    const markets = new Map<string, MockLocationRow[]>();
    regionRows.forEach((row) => {
      const marketRows = markets.get(row.market) ?? [];
      marketRows.push(row);
      markets.set(row.market, marketRows);
    });
    markets.forEach((marketRows, market) => {
      const marketExpandKey = `market-${region}-${market}`;
      const marketTotal = marketRows.reduce((total, row) => total + row.totalLoads, 0);
      const marketNonAsn = marketRows.reduce((total, row) => total + row.nonAsnLoads, 0);
      // Market group row: set region and market to their names; clear location
      // so that the location column cell is blank on this rollup row.
      result.push({
        ...marketRows[0],
        id: marketExpandKey,
        region,
        market,
        location: '',
        nonAsnLoads: marketNonAsn,
        totalLoads: marketTotal,
        pyPct: marketRows.reduce((total, row) => total + row.pyPct, 0) / marketRows.length,
        avgSkus: marketRows.reduce((total, row) => total + row.avgSkus, 0) / marketRows.length,
        caseQty: marketRows.reduce((total, row) => total + row.caseQty, 0),
        hierarchyLevel: 1,
        parentId: region,
        groupType: 'market',
        parentRegionExpandKey: regionExpandKey,
        parentMarketExpandKey: undefined,
      });
      marketRows.forEach((row) =>
        result.push({
          ...row,
          hierarchyLevel: 2,
          parentId: marketExpandKey,
          groupType: 'location',
          parentMarketExpandKey: marketExpandKey,
          parentRegionExpandKey: regionExpandKey,
        })
      );
    });
  });
  return result;
};

export const buildMarketHierarchyRows = (sourceRows: MockLocationRow[]): MockLocationRow[] => {
  const markets = new Map<string, MockLocationRow[]>();
  
  sourceRows.forEach((row) => {
    const key = `${row.region}|${row.market}`;
    const marketRows = markets.get(key) ?? [];
    marketRows.push(row);
    markets.set(key, marketRows);
  });

  const result: MockLocationRow[] = [];

  Array.from(markets.entries())
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .forEach(([key, marketRows]) => {
      const [region, market] = key.split('|');
      const marketExpandKey = `market-${region}-${market}`;
      const marketTotal = marketRows.reduce((total, row) => total + row.totalLoads, 0);
      const marketNonAsn = marketRows.reduce((total, row) => total + row.nonAsnLoads, 0);

      // Market group row: set region and market to their names; clear location
      // so that the location column cell is blank on this rollup row.
      result.push({
        ...marketRows[0],
        id: marketExpandKey,
        region,
        market,
        location: '',
        nonAsnLoads: marketNonAsn,
        totalLoads: marketTotal,
        pyPct: marketRows.reduce((total, row) => total + row.pyPct, 0) / marketRows.length,
        avgSkus: marketRows.reduce((total, row) => total + row.avgSkus, 0) / marketRows.length,
        caseQty: marketRows.reduce((total, row) => total + row.caseQty, 0),
        hierarchyLevel: 1,
        parentId: undefined,
        groupType: 'market',
        parentMarketExpandKey: undefined,
        parentRegionExpandKey: undefined,
      });

      marketRows.forEach((row) =>
        result.push({
          ...row,
          groupType: 'location',
          hierarchyLevel: 2,
          parentMarketExpandKey: marketExpandKey,
          parentRegionExpandKey: undefined,
        })
      );
    });

  return result;
};

